/**
 * BootstrapContext - `GET /v1/bootstrap`, loaded once at boot.
 *
 * Exposes the server's flags, offers, polling hints and region verdict. A blocked region
 * replaces the whole site with the server's own message. A failed bootstrap never blocks the
 * site: the content routes have their own data and fail on their own.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, unwrap } from '../api';

const BootstrapContext = createContext({ bootstrap: null, loading: true, error: null });

/** Shown only when the server sends no message of its own. */
export const REGION_BLOCKED_FALLBACK = 'BKFC+ is not available in your region.';

export const RegionBlockedPage = ({ message }) => (
  <div className="flex items-center justify-center min-h-screen p-6 bg-primary text-white">
    <div className="max-w-md text-center" role="alert">
      <h1 className="mb-4 text-3xl font-display uppercase">Not available</h1>
      <p className="text-white/80">{message?.value || REGION_BLOCKED_FALLBACK}</p>
    </div>
  </div>
);

export function BootstrapProvider({ children }) {
  const [state, setState] = useState({ bootstrap: null, loading: true, error: null });

  useEffect(() => {
    let active = true;
    unwrap(api.GET('/v1/bootstrap'))
      .then(body => active && setState({ bootstrap: body.data, loading: false, error: null }))
      .catch(error => {
        console.error('BootstrapProvider: GET /v1/bootstrap failed:', error);
        if (active) {
          setState({ bootstrap: null, loading: false, error });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => {
    const b = state.bootstrap;
    return {
      ...state,
      flags: b?.flags ?? {},
      offers: b?.offers ?? null,
      region: b?.region ?? null,
      polling: b?.polling ?? null,
    };
  }, [state]);

  if (value.region?.blocked) {
    return <RegionBlockedPage message={value.region.message} />;
  }
  return <BootstrapContext.Provider value={value}>{children}</BootstrapContext.Provider>;
}

export const useBootstrap = () => useContext(BootstrapContext);
